import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import GroupChannelHandler from "@sendbird/uikit-react/handlers/GroupChannelHandler";
import { GroupChannelProvider } from "@sendbird/uikit-react/GroupChannel/context";
import GroupChannelUI from "@sendbird/uikit-react/GroupChannel/components/GroupChannelUI";
import MessageList, { GroupChannelMessageListProps } from "@sendbird/uikit-react/GroupChannel/components/MessageList";
import "@sendbird/uikit-react/dist/index.css";
import { iChanelProp } from "../../types/chatTypes";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import SendbirdSendMsgIconSvg from "../../assets/svg-fc/SendbirdSendMsgIconSvg";
import { Avatar, Box, Divider, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import { getStringsFirstLetter } from "../../utils/commonUtils";
import typography from "../../theme/typography";
import palette, {
	SENDBIRD_MENTION_BACKGROUND_LOGO_COLORS,
	SENDBIRD_MENTION_TEXT_COLORS,
} from "../../theme/palette";
import { SendbirdMember, SendbirdMemberMiscInfo } from "../../interfaces/sendbird";
import { formatSendbirdSenderNameRole, getLoggedInVendorDisplayName, parseVendorNameFromSendbirdChannelName } from "../../helpers/sendbirdDisplay";
import { createRenderSendbirdMessageContent } from "./renderSendbirdMessageContent";
import { useFocusSendbirdMessageInput } from "../../utils/sendbird/focusSendbirdMessageInput";
import SendbirdScrollToFirstUnreadOnOpen from "./SendbirdScrollToFirstUnreadOnOpen";
import ChatMediaViewerHost from "./ChatMediaViewerHost";
import useUserInfo from "../../hooks/reduxHelpersHooks/useUserInfo";
import { ConversationMessagesSkeleton } from "../pages-components/chat-page-components/MessagingSkeleton/ConversationSkeleton";
import "./userMentionItem.scss";

/** Sendbird UIKit pubSub topic names (runtime strings; not exported from the package). */
const SENDBIRD_PUBSUB = {
	SEND_USER_MESSAGE: "SEND_USER_MESSAGE",
	SEND_FILE_MESSAGE: "SEND_FILE_MESSAGE",
} as const;

type SendbirdPubSubPayload = { channel?: { url?: string }; message?: unknown };
type SendbirdPubSubSubscribe = (
	topic: string,
	listener: (payload: SendbirdPubSubPayload) => void,
) => { remove: () => void };

const Channel: FC<iChanelProp> = ({
	url,
	onMessageUpdates,
	onSendChat,
	onChannelPreviewUpdate,
	showLatestIcon = false,
}) => {
	const sendbirdState = useSendbirdStateContext();
	const { stores, config } = sendbirdState;
	const sdk = stores?.sdkStore?.sdk;
	const pubSub = config?.pubSub;
	const connectedUserId = config?.userId ?? "";
	const { userDetails } = useUserInfo();
	const loggedInVendorName = getLoggedInVendorDisplayName(userDetails);
	const [channelMembers, setChannelMembers] = useState<SendbirdMemberMiscInfo[]>([]);
	const [channelVendorName, setChannelVendorName] = useState<string | null>(null);
	const channelRootRef = useRef<HTMLDivElement>(null);

	useFocusSendbirdMessageInput(!!url, url, channelRootRef);

	const handleMessageUpdates = useCallback(() => {
		onMessageUpdates?.();
	}, [onMessageUpdates]);

	async function getChannelMembers(channelUrl: string) {
		try {
			const channel = await sdk?.groupChannel?.getChannel(channelUrl);
			setChannelVendorName(parseVendorNameFromSendbirdChannelName(channel?.name));
			const memberQuery = channel.createMemberListQuery();
			const members = await memberQuery.next();
			const updatedMembers = (members || []) as unknown as SendbirdMemberMiscInfo[];
			return updatedMembers
				?.filter((member: SendbirdMemberMiscInfo) => member.userId !== connectedUserId)
				.map((member, index) => {
					const BG_COLORS = Object.values(SENDBIRD_MENTION_BACKGROUND_LOGO_COLORS);
					const bg = BG_COLORS[index % BG_COLORS.length];
					const text = SENDBIRD_MENTION_TEXT_COLORS[bg];

					return {
						userId: member.userId,
						bg,
						text,
					};
				});
		} catch (error) {
			console.error("Failed to load channel members:", error);
			return [];
		}
	}

	useEffect(() => {
		if ((!onSendChat && !onChannelPreviewUpdate) || !pubSub) return;

		const handleSent = (payload: SendbirdPubSubPayload) => {
			if (payload?.channel?.url === url) {
				onSendChat?.();
				onChannelPreviewUpdate?.(url, payload.message);
			}
		};

		const subscribe = pubSub.subscribe as SendbirdPubSubSubscribe;
		const userSub = subscribe(SENDBIRD_PUBSUB.SEND_USER_MESSAGE, handleSent);
		const fileSub = subscribe(SENDBIRD_PUBSUB.SEND_FILE_MESSAGE, handleSent);

		return () => {
			userSub.remove();
			fileSub.remove();
		};
	}, [onSendChat, onChannelPreviewUpdate, pubSub, url]);

	useEffect(() => {
		if (!sdk?.groupChannel || !url) return;

		let cancelled = false;

		getChannelMembers(url).then((members) => {
			if (!cancelled) {
				setChannelMembers(members);
			}
		});

		const handler = new GroupChannelHandler({
			onMessageReceived: () => {
				handleMessageUpdates();
			},
			onMessageUpdated: () => {
				handleMessageUpdates();
			},
			onMessageDeleted: () => {
				handleMessageUpdates();
			},
		});

		sdk.groupChannel.addGroupChannelHandler(url, handler);

		return () => {
			cancelled = true;
			sdk.groupChannel.removeGroupChannelHandler(url);
		};
	}, [sdk, url, connectedUserId, handleMessageUpdates]);

	const sendbirdDisplayOptions = useMemo(
		() => ({ channelVendorName, loggedInVendorName }),
		[channelVendorName, loggedInVendorName],
	);

	const [openedMediaMessageId, setOpenedMediaMessageId] = useState<number | null>(null);
	const closeMediaViewer = useCallback(() => setOpenedMediaMessageId(null), []);

	const groupChannelProps = useMemo(
		() => ({
			channelUrl: url,
			renderPlaceholderLoader: () => <ConversationMessagesSkeleton />,
			renderMessageList: (messageListProps: GroupChannelMessageListProps) => (
				<MessageList
					{...messageListProps}
					className="sendbird-conversation__message-list"
					renderPlaceholderLoader={() => <ConversationMessagesSkeleton withInput={false} />}
				/>
			),
			renderMessageContent: createRenderSendbirdMessageContent(sendbirdDisplayOptions, setOpenedMediaMessageId),
			renderUserMentionItem: ({ user }: { user: { userId: string } }) => (
				<UserMentionItem
					user={user as unknown as SendbirdMember}
					sendbirdDisplayOptions={sendbirdDisplayOptions}
					memberOtherInfo={
						channelMembers.find((member) => member.userId === user.userId) as unknown as SendbirdMemberMiscInfo
					}
				/>
			),
			...(showLatestIcon && {
				renderSendMessageIcon: () => <SendbirdSendMsgIconSvg />,
			}),
		}),
		[url, sendbirdDisplayOptions, channelMembers, showLatestIcon],
	);

	return (
		<div ref={channelRootRef} className="sendbirdChannelRoot">
			<GroupChannelProvider key={url} {...groupChannelProps}>
				<SendbirdScrollToFirstUnreadOnOpen />
				<GroupChannelUI {...groupChannelProps} />
				<ChatMediaViewerHost openedMessageId={openedMediaMessageId} onClose={closeMediaViewer} />
			</GroupChannelProvider>
		</div>
	);
};

const UserMentionItem: FC<{
	user: SendbirdMember;
	sendbirdDisplayOptions?: { channelVendorName?: string | null; loggedInVendorName?: string | null };
	memberOtherInfo: SendbirdMemberMiscInfo;
}> = ({ user, sendbirdDisplayOptions, memberOtherInfo }) => {
	return (
		<Box sx={{ width: "100%" }}>
			<Box className="userMentionBox">
				<ListItem
					sx={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
						width: "100%",
						padding: "16px 20px",
						"& .MuiListItemAvatar-root": {
							minWidth: "40px",
						},
					}}
				>
					<ListItemAvatar>
						{user?.plainProfileUrl ? (
							<Avatar src={user?.plainProfileUrl} />
						) : (
							<Avatar
								sx={{
									backgroundColor: memberOtherInfo?.bg || palette.grey[1150],
									color: memberOtherInfo?.text || palette.background.paper,
									width: "40px",
									height: "40px",
									fontSize: "16px",
									fontWeight: typography.fontWeightNormal,
									fontFamily: typography.fontPrimaryGeneralSans,
								}}
							>
								{getStringsFirstLetter(
									user?.nickname?.split(" ")[0] || "",
									user?.nickname?.split(" ")[1] || "",
								)}
							</Avatar>
						)}
					</ListItemAvatar>
					<ListItemText
						sx={{
							flex: 1,
							minWidth: 0,
							margin: 0,
							fontSize: "16px",
							fontWeight: typography.fontWeightNormal,
							color: palette.grey[1150],
							fontFamily: typography.fontPrimaryGeneralSans,
						}}
						primary={formatSendbirdSenderNameRole(user, sendbirdDisplayOptions)}
					/>
				</ListItem>
			</Box>
			<Divider
				sx={{
					margin: "0px",
					width: "100%",
					height: "1px",
					backgroundColor: palette.common.black,
				}}
			/>
		</Box>
	);
};

export default Channel;
